import type { TypographyProps } from "../../atoms/typography";
import { minTouchTargetClassName } from "../../lib/touch-target";
import { iconSize, spacing, touchTarget } from "../../tokens";
import type { StepperAccessibilityContext, StepperSize } from "./types";

type TypographyColor = NonNullable<TypographyProps["color"]>;
type TypographyVariant = NonNullable<TypographyProps["variant"]>;

/**
 * Stepper's own buttons (ruling J4; molecules audit Stepper #3), as Figma
 * draws them: a 12 or 16 icon with `padding-xs` 4 around it, so 20 or 24, and
 * no desktop step (Ardhi's DQ16 (a) answer, 2026-11-03)
 */
export const stepperIconSize: Record<StepperSize, number> = { sm: iconSize.xs, md: iconSize.sm };
const stepperBox: Record<StepperSize, number> = {
  sm: stepperIconSize.sm + 2 * spacing.xs,
  md: stepperIconSize.md + 2 * spacing.xs,
};
export const stepperBoxClasses: Record<StepperSize, string> = {
  sm: "h-icon-md w-icon-md",
  md: "h-icon-lg w-icon-lg",
};

/**
 * Figma's Stepper buttons, with the shared focus ring and a 44 tap area on
 * touch screens. Each look is chosen from `disabled` (as IconButton's), not
 * with `disabled:` classes, which iOS and Android don't apply
 */
export const stepperButtonClasses = [
  "rounded-radius-full relative flex shrink-0 items-center justify-center border border-solid transition-colors",
  "web:outline-none web:focus-visible:ring-focus web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-offset-focus",
  minTouchTargetClassName,
].join(" ");

/**
 * Enabled: white with the CTA2 border and a brand icon, and the shared fill
 * when hovered or pressed (DQ3; Ardhi's answers, 2026-11-03, item 3).
 * Disabled: the button family's (DQ11)
 */
export const stepperButtonLook = (disabled: boolean) =>
  disabled
    ? "bg-luce-button-disabled-ctaDisabled border-luce-button-disabled-ctaBorderDisabled cursor-not-allowed"
    : "bg-luce-brand-onPrimary border-luce-button-cta2-border [@media(hover:hover)]:hover:bg-luce-neutral-background-intermediate active:bg-luce-neutral-background-intermediate";

export const stepperIconColor = (disabled: boolean) =>
  disabled ? "text-luce-button-disabled-onCtaDisabled" : "text-luce-brand-primary";

/** The count: `caption-md` and `body-sm` (DQ16 (b)), in Figma's value frame. */
export const stepperCountVariant: Record<StepperSize, TypographyVariant> = {
  sm: "caption-md",
  md: "body-sm",
};
export const stepperValueClasses: Record<StepperSize, string> = {
  sm: "w-stepper-value-sm",
  md: "w-stepper-value-md",
};

/** As Figma and DS-027 draw it: intermediate at min, attenuated when disabled. */
export function getStepperCountColor(
  value: number,
  min: number,
  disabled: boolean,
): TypographyColor {
  if (disabled) return "neutral-foreground-attenuated";
  if (value <= min) return "neutral-foreground-intermediate";
  return "neutral-foreground-default";
}

// Each button takes taps 44 across on iOS and Android

export const stepperHitSlop: Record<StepperSize, number> = {
  sm: (touchTarget - stepperBox.sm) / 2,
  md: (touchTarget - stepperBox.md) / 2,
};

export function getStepperAccessibilityLabel(context: StepperAccessibilityContext): string {
  if (context.accessibilityLabel) return context.accessibilityLabel;
  return `Quantity: ${context.value}`;
}

/** The count's name on iOS and Android, where the group isn't announced. */
export function getStepperCountLabel(context: StepperAccessibilityContext): string {
  return `${context.accessibilityLabel ?? "Quantity"}: ${context.value}`;
}

/** "Decrease quantity", or "Decrease {accessibilityLabel}" as given. */
export function getStepperButtonLabels(accessibilityLabel?: string) {
  const subject = accessibilityLabel ?? "quantity";
  return { decrease: `Decrease ${subject}`, increase: `Increase ${subject}` };
}

/** A press emits a value inside [min, max], so it recovers from either side. */
export function clampStepperValue(next: number, min: number, max?: number): number {
  return Math.min(max ?? Infinity, Math.max(min, next));
}

export function isDecrementDisabled(value: number, min: number, disabled?: boolean): boolean {
  if (disabled) return true;
  return value <= min;
}

export function isIncrementDisabled(value: number, max?: number, disabled?: boolean): boolean {
  if (disabled) return true;
  if (max !== undefined && value >= max) return true;
  return false;
}
